import { useState, useEffect, useMemo, useCallback } from "react";
import { getAdminCreds } from "@/lib/ddfApi";
import { isoDate } from "./analyticsUtils";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function authHeaders() {
  const creds = getAdminCreds();
  return {
    "X-Admin-Email": creds?.email || "",
    "X-Admin-Password": creds?.password || "",
  };
}

// Fetch a file (blob) from an authed endpoint and trigger a browser download.
async function downloadBlob(url, filename) {
  const r = await fetch(url, { headers: authHeaders() });
  if (!r.ok) throw new Error(`HTTP ${r.status}`);
  const blob = await r.blob();
  const objUrl = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = objUrl;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(objUrl);
}

/**
 * useAnalytics — owns all analytics data-fetching + related side-effect state
 * (summary, subscriber list, CSV/PDF/XLSX exports, email report) plus derived
 * chart data. The caller owns the date-range UI and passes (start, end) into
 * the action functions.
 */
export function useAnalytics() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [subsOpen, setSubsOpen] = useState(false);
  const [subsList, setSubsList] = useState([]);
  const [subsLoading, setSubsLoading] = useState(false);

  const [emailTo, setEmailTo] = useState("");
  const [emailSending, setEmailSending] = useState(false);
  const [emailStatus, setEmailStatus] = useState(null); // {ok, msg}

  const fetchSummary = useCallback(async (s, e) => {
    setLoading(true);
    setError(null);
    try {
      const r = await fetch(
        `${BACKEND_URL}/api/admin/analytics/summary?start=${isoDate(s)}&end=${isoDate(e)}`,
        { headers: authHeaders() }
      );
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      setData(await r.json());
    } catch (err) {
      setError(err.message || "Failed to load analytics");
    } finally {
      setLoading(false);
    }
  }, []);

  const exportReport = useCallback(async (fmt, s, e) => {
    try {
      await downloadBlob(
        `${BACKEND_URL}/api/admin/analytics/export?start=${isoDate(s)}&end=${isoDate(e)}&fmt=${fmt}`,
        `ddf-analytics.${fmt}`
      );
    } catch (err) {
      if (process.env.NODE_ENV !== "production") console.error("Export failed", err);
    }
  }, []);

  const downloadSubsXlsx = useCallback(async (s, e) => {
    try {
      await downloadBlob(
        `${BACKEND_URL}/api/admin/subscribers/export.xlsx?start=${isoDate(s)}&end=${isoDate(e)}`,
        "ddf-subscribers.xlsx"
      );
    } catch (err) {
      if (process.env.NODE_ENV !== "production") console.error("XLSX export failed", err);
    }
  }, []);

  const openSubsList = useCallback(async (s, e) => {
    setSubsOpen(true);
    setSubsLoading(true);
    try {
      const r = await fetch(
        `${BACKEND_URL}/api/admin/subscribers?start=${isoDate(s)}&end=${isoDate(e)}`,
        { headers: authHeaders() }
      );
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const json = await r.json();
      setSubsList(json.items || []);
    } catch (err) {
      if (process.env.NODE_ENV !== "production") console.error(err);
      setSubsList([]);
    } finally {
      setSubsLoading(false);
    }
  }, []);

  const sendReport = useCallback(async (s, e) => {
    if (!emailTo) return;
    setEmailSending(true);
    setEmailStatus(null);
    try {
      const r = await fetch(`${BACKEND_URL}/api/admin/analytics/email`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...authHeaders() },
        body: JSON.stringify({ recipient: emailTo, start: isoDate(s), end: isoDate(e) }),
      });
      const body = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(body.detail || `HTTP ${r.status}`);
      setEmailStatus({ ok: true, msg: `Report successfully sent to ${emailTo}` });
    } catch (err) {
      const m = err.message || "Please check the email address and try again.";
      setEmailStatus({ ok: false, msg: /^failed to send/i.test(m) ? m : `Failed to send. ${m}` });
    } finally {
      setEmailSending(false);
    }
  }, [emailTo]);

  // ---- derived chart data ----
  const pieData = useMemo(
    () => (data?.trafficSources || []).map((s) => ({ name: s.source, value: s.count, pct: s.pct })),
    [data]
  );

  const lastUpdatedStr = useMemo(() => {
    if (!data?.lastUpdated) return "";
    try {
      return new Date(data.lastUpdated).toLocaleString("en-GB", {
        timeZone: "Asia/Dubai",
        dateStyle: "medium",
        timeStyle: "short",
      });
    } catch {
      return data.lastUpdated;
    }
  }, [data]);

  const maxDrawClicks = useMemo(
    () => Math.max(1, ...((data?.drawButtonClicks || []).map((d) => d.rangeClicks))),
    [data]
  );

  return {
    data,
    loading,
    error,
    fetchSummary,
    exportReport,
    downloadSubsXlsx,
    subsOpen,
    setSubsOpen,
    subsList,
    subsLoading,
    openSubsList,
    emailTo,
    setEmailTo,
    emailSending,
    emailStatus,
    sendReport,
    pieData,
    lastUpdatedStr,
    maxDrawClicks,
  };
}
